// src/components/Tools/Results/WhoisResults.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

function getPayload(results: any[]): Record<string, any> | null {
  if (!Array.isArray(results) || results.length === 0) return null;
  const first = results[0];
  return (first && typeof first === 'object') ? first : null;
}

function fmt(v: any) {
  if (v === null || v === undefined) return '-';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

const WhoisResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const data = getPayload(results);

  if (!data) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.whois.empty')}</p>;
  }

  const rows: Array<[string, string]> = [
    [t('tools.results_scan.whois.domain_name'), fmt(data.domain_name)],
    [t('tools.results_scan.whois.registry_domain_id'), fmt(data.registry_domain_id)],
    [t('tools.results_scan.whois.registrar'), fmt(data.registrar)],
    [t('tools.results_scan.whois.registrar_url'), fmt(data.registrar_url)],
    [t('tools.results_scan.whois.registrar_whois_server'), fmt(data.registrar_whois_server)],
    [t('tools.results_scan.whois.creation_date'), fmt(data.creation_date)],
    [t('tools.results_scan.whois.updated_date'), fmt(data.updated_date)],
    [t('tools.results_scan.whois.registry_expiry_date'), fmt(data.registry_expiry_date)],
    [t('tools.results_scan.whois.domain_status'), fmt(data.domain_status)],
    [t('tools.results_scan.whois.name_server'), fmt(data.name_server)],
    [t('tools.results_scan.whois.dnssec'), fmt(data.dnssec)],
    [t('tools.results_scan.whois.registrar_abuse_contact_email'), fmt(data.registrar_abuse_contact_email)],
    [t('tools.results_scan.whois.registrar_abuse_contact_phone'), fmt(data.registrar_abuse_contact_phone)],
    // Registrant
    [t('tools.results_scan.whois.registrant_name'), fmt(data.registrant_name)],
    [t('tools.results_scan.whois.registrant_organization'), fmt(data.registrant_organization)],
    [t('tools.results_scan.whois.registrant_city'), fmt(data.registrant_city)],
    [t('tools.results_scan.whois.registrant_state_province'), fmt(data['registrant_state/province'] ?? data.registrant_state_province)],
    [t('tools.results_scan.whois.registrant_country'), fmt(data.registrant_country)],
    [t('tools.results_scan.whois.registrant_email'), fmt(data.registrant_email)],
    [t('tools.results_scan.whois.registrant_phone'), fmt(data.registrant_phone)],
    // Admin
    [t('tools.results_scan.whois.admin_name'), fmt(data.admin_name)],
    [t('tools.results_scan.whois.admin_organization'), fmt(data.admin_organization)],
    [t('tools.results_scan.whois.admin_city'), fmt(data.admin_city)],
    [t('tools.results_scan.whois.admin_country'), fmt(data.admin_country)],
    [t('tools.results_scan.whois.admin_email'), fmt(data.admin_email)],
    [t('tools.results_scan.whois.admin_phone'), fmt(data.admin_phone)],
    // Tech
    [t('tools.results_scan.whois.tech_name'), fmt(data.tech_name)],
    [t('tools.results_scan.whois.tech_organization'), fmt(data.tech_organization)],
    [t('tools.results_scan.whois.tech_city'), fmt(data.tech_city)],
    [t('tools.results_scan.whois.tech_country'), fmt(data.tech_country)],
    [t('tools.results_scan.whois.tech_email'), fmt(data.tech_email)],
    [t('tools.results_scan.whois.tech_phone'), fmt(data.tech_phone)],
  ];

  // ¿Todo vacío? (todo '-' o string vacío)
  const hasAnyValue = rows.some(([, v]) => {
    const s = String(v).trim();
    return s !== '-' && s !== '';
  });

  if (!hasAnyValue) {
    return <p className="text-sm text-slate-500 text-center">{t('tools.results_scan.whois.no_data')}</p>;
  }

  return (
    <div className="w-full">
      <div className="overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gradient-to-r from-sky-50 to-indigo-50">
            <tr className="text-slate-700">
              <th className="px-3 py-2 text-center uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.whois.field')}
              </th>
              <th className="px-3 py-2 text-center uppercase tracking-wide text-[11px]">
                {t('tools.results_scan.whois.value')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.map(([k, v], i) => (
              <tr key={`${k}-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                <td className="px-3 py-2 font-medium text-slate-900">{k}</td>
                <td className="px-3 py-2 text-slate-700 text-center break-words whitespace-pre-wrap">
                  {v}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WhoisResults;
